import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle, Loader } from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

export default function SubscriptionSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('loading');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('error');
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await api.get(`/stripe/session/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        await refreshUser();
        toast.success('Subscription activated!');
        setTimeout(() => navigate('/athletes'), 3000);
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(() => pollPaymentStatus(sessionId), pollInterval);
    } catch (error) {
      console.error('Error checking payment:', error);
      setAttempts(prev => prev + 1);
      setTimeout(() => pollPaymentStatus(sessionId), pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-[#000] flex items-center justify-center p-4">
      <div className="text-center">
        {status === 'loading' && (
          <>
            <Loader className="w-16 h-16 text-[#00E5E5] mx-auto mb-4 animate-spin" />
            <h1 className="text-2xl font-bold text-white mb-2">Processing Payment...</h1>
            <p className="text-gray-400">Please wait while we confirm your subscription</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-[#4ADE80] mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Payment Successful!</h1>
            <p className="text-gray-400 mb-6">Your subscription is now active</p>
            <button
              onClick={() => navigate('/athletes')}
              className="bg-[#00E5E5] text-black font-bold py-3 px-8 rounded-xl hover:bg-[#5FFBFB] transition-colors"
            >
              Start Tracking
            </button>
          </>
        )}

        {status === 'timeout' && (
          <>
            <div className="w-16 h-16 bg-[#FF9500] rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">⏳</span>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Taking Longer Than Expected</h1>
            <p className="text-gray-400 mb-6">
              Your payment may still be processing. Check your email for confirmation.
            </p>
            <button
              onClick={() => navigate('/athletes')}
              className="bg-[#00E5E5] text-black font-bold py-3 px-8 rounded-xl hover:bg-[#5FFBFB] transition-colors"
            >
              Go to Athletes
            </button>
          </>
        )}

        {status === 'expired' && (
          <>
            <div className="w-16 h-16 bg-[#FF5252] rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">✕</span>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Session Expired</h1>
            <p className="text-gray-400 mb-6">
              This payment session has expired. Please try again.
            </p>
            <button
              onClick={() => navigate('/paywall')}
              className="bg-[#00E5E5] text-black font-bold py-3 px-8 rounded-xl hover:bg-[#5FFBFB] transition-colors"
            >
              Try Again
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-[#FF5252] rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">!</span>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Something Went Wrong</h1>
            <p className="text-gray-400 mb-6">
              We couldn't verify your payment. Please contact support.
            </p>
            <button
              onClick={() => navigate('/athletes')}
              className="bg-[#00E5E5] text-black font-bold py-3 px-8 rounded-xl hover:bg-[#5FFBFB] transition-colors"
            >
              Go Back
            </button>
          </>
        )}
      </div>
    </div>
  );
}
